
import React from 'react';
import { Helmet } from 'react-helmet';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  structuredData?: Record<string, any>;
  noIndex?: boolean;
}

const SEOHead = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogImage = '/public/og-image.png',
  twitterCard = 'summary_large_image',
  structuredData,
  noIndex = false
}: SEOHeadProps) => {
  // Get the domain, preferring truehr.co if in production
  const getDomain = () => {
    if (typeof window === 'undefined') return 'https://truehr.co';
    
    const host = window.location.hostname;
    if (host.includes('truehr.co')) {
      return 'https://truehr.co';
    }
    return window.location.origin;
  };
  
  const domain = getDomain();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
  const canonicalLink = canonicalUrl || `${domain}${currentPath}`;
  
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalLink} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalLink} />
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : `${domain}${ogImage}`} />
      
      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : `${domain}${ogImage}`} />
      
      {/* No Index if required */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Structured Data for Rich Results */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
